import { useEffect } from "react";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { SLUG_TO_ROLE, basePathForRole } from "@/lib/roleRoutes";
import OrgDashboard from "@/pages/OrgDashboard";
import SalesDashboard from "@/pages/SalesDashboard";
import DealerDashboard from "@/pages/DealerDashboard";
import DistributorDashboard from "@/pages/DistributorDashboard";
import WarehouseDashboard from "@/pages/WarehouseDashboard";
import VendorDashboard from "@/pages/VendorDashboard";
import BuyerDashboard from "@/pages/BuyerDashboard";

// Pick the right dashboard based on user.role
function dashboardFor(role) {
  if (["ORG_ADMIN", "PROCUREMENT_MANAGER", "FINANCE_MANAGER", "INVENTORY_MANAGER", "PRODUCTION_MANAGER"].includes(role)) return OrgDashboard;
  if (["SALES_MANAGER", "SALES_EXECUTIVE", "SALES_COORDINATOR"].includes(role)) return SalesDashboard;
  if (["WAREHOUSE_MANAGER", "WAREHOUSE_EXECUTIVE"].includes(role)) return WarehouseDashboard;
  if (role === "DISTRIBUTOR") return DistributorDashboard;
  if (role === "DEALER") return DealerDashboard;
  if (role === "VENDOR_ADMIN" || role === "VENDOR_STAFF") return VendorDashboard;
  if (role === "BUYER") return BuyerDashboard;
  return null;
}

// /<tenant-slug>/<role-slug>/* — tenant-scoped role workspace
export default function TenantRoleWorkspace() {
  const { tenantSlug, roleSlug } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const expectedRole = SLUG_TO_ROLE[roleSlug];

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate(`/${tenantSlug}/${roleSlug}/login`, { replace: true });
      return;
    }
    // Enforce tenant + role match
    if (user.role !== expectedRole) {
      navigate(basePathForRole(user.role), { replace: true });
    }
  }, [user, loading, expectedRole, tenantSlug, roleSlug, navigate]);

  if (loading || !user) return null;
  if (!expectedRole) return <Navigate to="/" replace />;
  if (user.role !== expectedRole) return null; // redirect handled in effect

  const Dashboard = dashboardFor(user.role);
  if (!Dashboard) return <Navigate to="/" replace />;
  return <Dashboard />;
}
